import React, {useState, useEffect, useRef} from "react";
import { FaTimes as FaClose, FaFilter, FaTimes, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import server from '../../../api/server';
import '../../../dashboard-components/loader.css';
import { useNavigate, useLocation } from 'react-router-dom';
import ModernTimeDropdown from "../../../dashboard-components/layouts/modernDropdown";
import { useToast } from '../../../context/ToastContext';
import { Download, Edit, Trash2 } from 'lucide-react';
import { initializeFiltersFromUrl, updateUrlWithFilters } from '../../../utils/filterUtils';
import CreatePhoneNumberModal from "./phone-numbers/CreatePhoneNumberModal.jsx";
import PhoneNumberActionConfirmModal from "./phone-numbers/PhoneNumberActionConfirmModal.jsx";

import ManagePhoneNumberModal from "./phone-numbers/ManagePhoneNumberModal.jsx";

const PhoneNumberRecords = ({serviceID, clientID, segment, type}) => {
  const { showToast } = useToast();
  const [manageNumber, setManageNumber] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Default filter values
  const defaultFilters = {
    agent: '',
    phone_number: '',
    date: 'Last 7 Days',
    customStart: '',
    customEnd: ''
  };

  const defaultAdvancedFilters = {
    phone_number: ''
  };

  // Initialize filters from URL or use defaults (only when not passed via segment prop)
  const initialFilterState = !segment ? initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters) : {
    filters: defaultFilters,
    advancedFilters: defaultAdvancedFilters,
    customStart: '',
    customEnd: ''
  };
  const [filteredPhoneNumbers, setFilteredPhoneNumbers] = useState([]);
  const [selectedAliasName, setSelectedAliasName] = useState('');
    const [email, setEmail] = useState(null)
    const [client_id, setClient_id] = useState(null)
    const [service_id, setService_id] = useState(null)
    const [totalRecords, setTotalRecords] = useState(0)
    const [originalSize, setOriginalSize] = useState(0)
    const [openType, setOpenType] = useState(null)
    const [showPhoneNumbersPage, setShowPhoneNumbersPage] = useState(false)
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null)
    const [segmentParameterData, setSegmentParameterData] = useState({'type':null, 'phone_number': null, 'name': null})
    const [role] = useState(()=> {
      const userCookie = getCookie('dashboard_login_user_creds');
      if (userCookie) {
        const userData = JSON.parse(userCookie);
        const role = userData.role
        return role
      }
      return 'admin';
    })
    const [filters, setFilters] = useState(initialFilterState.filters);
    const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);
      const [page, setPage] = useState(1);
      const [totalPages, setTotalPages] = useState(1);
      const [apiCallRunning, setApiCallRunning] = useState(false)
      const [loading, setLoading] = useState(false)
      const [showEditPhoneNumberModal, setShowEditPhoneNumberModal] = useState(false)
      const [showBuyConfirm, setShowBuyConfirm] = useState(false)
      const [isBuyingNumber, setIsBuyingNumber] = useState(false)
      // Ref for aborting customer page requests
      const phoneNumbersAbortRef = useRef(null);
      const [dummyFilterData, setDummyFilterData] = useState({
        agents: [],
        date: ['Today',
          'Yesterday',
          'This Week',
          "This Month",
          "Last Month",
          "Last 7 Days",
          "Last 30 Days",
          "Last 3 Months",
          "Custom Range"]
      });
      
      // Update URL whenever filters change (only if not using segment prop)
      useEffect(() => {
        if (!segment && !type && client_id && service_id) { // Only update URL for standalone usage, not when embedded as modal
          // Debounce URL updates to avoid conflicts with navigation
          const timeoutId = setTimeout(() => {
            updateUrlWithFilters(navigate, location, filters, advancedFilters, '', '');
          }, 300);
          
          return () => clearTimeout(timeoutId);
        }
      }, [filters, advancedFilters, segment, type, client_id, service_id]); // Removed navigate and location from deps

      function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }
    const downloadConversations = async () => {
      showToast('Download is in progress. Please wait', "success")
        let toDownloadData = []
        let res = await server.getDownloadPhoneNumbersData(client_id, service_id, 1, filters.agent, filters.date, filters.phone_number, filters.customStart, filters.customEnd)
        if(res.status === 200){
          let resp = await res.json()
          if(resp.data){
            toDownloadData = resp.data
          }
        }else{
          showToast('Something went wrong. Please try again later', 'error')
        }
      // Determine which dataset to use
      const data = toDownloadData;
      
      if (!data || data.length === 0) {
        console.error(`No data available`);
        return;
      }
      
      // Get all possible headers from the objects
      const headers = Array.from(
        new Set(
          data.flatMap(item => Object.keys(item))
        )
      );
      
      // Create CSV content
      // First add the headers
      let csvContent = '\uFEFF'; // Add BOM for Excel to handle UTF-8 better
      csvContent += headers.join(',') + '\n';
      
      // Add each row of data
      data.forEach(item => {
        const row = headers.map(header => {
          // Handle missing values, quotes in strings, and format properly for CSV
          const value = item[header] === undefined ? '' : item[header];
          // Properly escape values that contain commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvContent += row.join(',') + '\n';
      });
      
      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create a download link and trigger the download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `calls_${new Date().toISOString().slice(0, 10)}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(url);
    };

    const handleQuickFilterChange = (e, key) => {
      const value = e.target.value;
      
      // If changing time filter and it's not "Custom Range", clear the custom date values
      if ((key === 'date') && value !== 'Custom Range') {
        setFilters((prev) => ({ 
          ...prev, 
          [key]: value,
          'Custom Start': '',
          'Custom End': ''
        }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    };
      const handleAdvancedInputChange = (e, key) => {
        const value = e.target.value;
        setAdvancedFilters((prev) => ({
          ...prev,
          [key]: value,
        }));
      };
    
    const formatEpochToReadableDate = (epoch) => {
        const date = new Date(epoch*1000); // Convert seconds to milliseconds
        const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
        return date.toLocaleString('en-GB', options).replace(',', '');
      };

    const formatEpochOrNA = (epoch) => {
      if (epoch === null || epoch === undefined || epoch === '' || epoch === 0) return 'NA';
      const date = new Date(epoch * 1000);
      if (Number.isNaN(date.getTime())) return 'NA';
      return formatEpochToReadableDate(epoch);
    };

    const handleBuyNewNumber = async () => {
      try {
        setIsBuyingNumber(true);
        const res = await server.buy_voice_agent_phone_number(
          client_id,
          service_id,
          email,
          'phone-numbers',
          undefined
        );
        if (res.status !== 200) {
          showToast('Unable to buy new phone number', 'error');
          return;
        }
        const resp = await res.json();
        const newNumber =
          resp?.data?.phone_number ||
          resp?.data?.new_phone_number ||
          resp?.phone_number ||
          resp?.new_phone_number;
        if (!newNumber) {
          showToast('No phone number returned from server', 'error');
          return;
        }
        showToast('New phone number purchased successfully', 'success');
        get_phone_numbers_page_details(client_id, service_id, page);
      } catch (error) {
        console.log(error);
        showToast('Unable to buy new phone number', 'error');
      } finally {
        setIsBuyingNumber(false);
      }
    };

    
    const get_phone_numbers_page_details = async (client_id, service_id, page_size) => {
      // abort previous
      if(phoneNumbersAbortRef.current){ phoneNumbersAbortRef.current.abort(); }
      const controller = new AbortController();
      phoneNumbersAbortRef.current = controller;

      try {
        setApiCallRunning(true);
        setLoading(true);

        const res = await server.get_voice_agent_phone_number_list(client_id, service_id, email, 'phone-numbers', undefined, page, filters.phone_number, filters.agent, filters.date, filters['Custom Start'], filters['Custom End']);
        if(res.status === 200){
          const resp = await res.json();
          setFilteredPhoneNumbers(resp.phone_numbers);
          const totalRecords = resp.total_records;
          setTotalRecords(totalRecords);
          setOriginalSize(totalRecords);
          setTotalPages(Math.ceil(totalRecords / 50));
          setLoading(false);
        }else{
          showToast('Something went wrong. Please try again later', 'error')
          setApiCallRunning(false)
          setLoading(false)
        }
        setApiCallRunning(false);
      }catch(error){
        if(error.name === 'AbortError'){
          setApiCallRunning(false);
          return;
        }
        setApiCallRunning(false);
        setLoading(false);
      }
    }

  // fetch filter details once
  const fetchFilterDetailsOncePhoneNumbers = async () => {
    try{
      const res = await server.getVoiceAgentsMetricsFilters(client_id, service_id, email, 'phone-numbers', undefined);
      if(res.status === 200){
        const data = await res.json();
        setDummyFilterData(prev=>({...prev, agents: data.agents}));
      }
    }catch(err){}
  }

  const onCreatePhoneNumber = () => {
    setShowEditPhoneNumberModal(false)
    get_phone_numbers_page_details(client_id, service_id, 1);
  }

  useEffect(()=>{
    if(client_id && email){
      fetchFilterDetailsOncePhoneNumbers();
    }
  }, [client_id, email]);
    
      const handleNextPage = () => {
        if (page < totalPages) {
            const newPage = page + 1;
            setPage(newPage); 
            get_phone_numbers_page_details(client_id, service_id, newPage); // ✅ Ensure only one call
        }
      };
    
      const handlePreviousPage = () => {
          if (page > 1) {
              const newPage = page - 1;
              setPage(newPage);
              get_phone_numbers_page_details(client_id, service_id, newPage); // ✅ Ensure only one call
          }
      };
      const isInitializingRef = useRef(false); // to prevent API calls during initialization
    
      useEffect(()=> {
        isInitializingRef.current = true;
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
          if (user) {
            setEmail(user.email);
          }
          if(serviceID){
            setService_id(serviceID)
          }
          if(clientID){
            setClient_id(clientID)
          }
          if(type){
            setOpenType(type)
          }
          if(segment){
            setSegmentParameterData(segment)
            if(segment.filter_applied?.date){
              handleQuickFilterChange({'target': {'value': segment.filter_applied.date}}, 'date')
              
              if(segment.filter_applied?.date === 'Custom Range'){
                setFilters((prev) => ({ ...prev, ['Custom Start']: segment.filter_applied['Custom Start'] }));
                setFilters((prev) => ({ ...prev, ['Custom End']: segment.filter_applied['Custom End'] }));
              }
            }
            if(segment.filter_applied?.agent){
              handleQuickFilterChange({'target': {'value': segment.filter_applied.agent}}, 'agent')
            }
            if(segment.filter_applied?.phone_number){
              handleAdvancedInputChange({'target': {'value': segment.filter_applied.phone_number}}, 'phone_number')
              if(segment.phone_number && segment.name){ 
                setAdvancedFilters((prev) => ({ ...prev, [segment.phone_number]: segment.name ? segment.name : "Yes" }));
              }
            }
          }
          if(role === 'agent'){
            handleQuickFilterChange({'target': {'value': user.name}}, 'agent')
          }
          const initTimeout = setTimeout(() => {
            isInitializingRef.current = false;
          }, 100);
          
          return () => {
            clearTimeout(initTimeout);
          };
      }, [serviceID, clientID, segment, role])

      // Handle page navigation
      useEffect(() => {
          if(client_id){
            get_phone_numbers_page_details(client_id, service_id, page);
          }
        }, [client_id, page]);
        const filtersDebounceRef = useRef(null);
        const firstFiltersLoadRef = useRef(true);
        useEffect(() => {
          if (!client_id) return;
      
          // Skip debounce logic on initial mount to avoid duplicate first fetch
          if (firstFiltersLoadRef.current) {
            firstFiltersLoadRef.current = false;
            return;
          }
          if (isInitializingRef.current) {
            return;
          }
      
          // Clear previous debounce timer
          if (filtersDebounceRef.current) {
            clearTimeout(filtersDebounceRef.current);
          }
      
          filtersDebounceRef.current = setTimeout(() => {
            // Only reset to first page; page effect will handle fetching once
            if (page !== 1) {
              setPage(1);
            } else {
                get_phone_numbers_page_details(client_id, service_id, 1);
            }
          }, 600); // idle delay
      
          return () => {
            if (filtersDebounceRef.current) {
              clearTimeout(filtersDebounceRef.current);
            }
          };
        }, [filters, advancedFilters, client_id]);
        const handleEditPhoneNumber = (phoneNumber) => {
          setSelectedPhoneNumber(phoneNumber)
          setSelectedAliasName(phoneNumber.alias_name)
          setShowEditPhoneNumberModal(true)
        }
    
    return (
        <div className="flex flex-col font-sans bg-white" style={{height: "100%"}}>
          {!showPhoneNumbersPage && <main className="flex-1 py-3 px-5 bg-gray-50">
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-gray-900 mb-2">Phone Numbers</h1>
                <p className="text-sm text-gray-500">View and manage all phone numbers</p>
            </div>

            {/* Active Filters */}
            {advancedFilters.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex flex-wrap gap-2">
                    {advancedFilters.map((filter, index) => (
                    <div key={index} className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm">
                        <span className="mr-1">{filter.type}:</span>
                        <span className="font-medium">{filter.value}</span>
                        <button 
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                        <FaClose className="h-3 w-3" />
                        </button>
                    </div>
                    ))}
                    {advancedFilters.length > 0 && (
                    <button 
                        className="text-sm text-blue-600 hover:text-blue-800"
                        onClick={() => setAdvancedFilters([])}
                    >
                        Clear all
                    </button>
                    )}
                </div>
                </div>
            )}

            {/* Primary Filter Section */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium text-gray-700">Quick Filters</div>
                <div className="flex space-x-3">
                {role === 'superadmin' && <button className="px-4 py-2 rounded text-sm text-white bg-indigo-600" onClick={() => setManageNumber({ number: null })}>Add Number</button>}
                {role === 'superadmin' && (
                  <button
                    onClick={() => setShowBuyConfirm(true)}
                    disabled={isBuyingNumber}
                    className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90"
                    style={{ backgroundColor: "rgb(79, 70, 229)" }}
                  >
                    {isBuyingNumber ? 'Buying...' : 'Buy New Number'}
                  </button>
                )}
                {openType && <button 
                  onClick={downloadConversations}
                  className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90"
                  style={{ backgroundColor: "rgb(79, 70, 229)" }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>}
                </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
              
                    <input class="global-input"value={filters.phone_number} type="text" placeholder="Phone Number" onChange={(e) => handleQuickFilterChange(e, 'phone_number')} 
                    className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input" />
                
                <ModernTimeDropdown isDisabled={role === 'agent'} placeholder="Agent" value={filters.agent} onChange={(e) => handleQuickFilterChange(e, 'agent')} options={dummyFilterData.agents}/>
                </div>
                {/* Filter Display */}
                <div className="flex gap-4 mb-4 mt-4 flex-wrap">
                    {Object.keys(filters).map(
                      (key) => {
                        if (key === 'date') {
                          return null;
                        }
                        if ((key === 'Custom Start' || key === 'Custom End') && filters.date !== 'Custom Range') {
                          return null;
                        }
                        return filters[key] && (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {filters[key]}</span>
                            {(key!=='date' && key !== segmentParameterData.phone_number && !(role === 'employee' && key === 'employee')) && <button
                              className="ml-2 text-gray-600"
                              onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>}
                          </div>
                        )
                      })}
                      {/* Show advanced filters that are set (excluding defaults) */}
                      {Object.entries(advancedFilters).map(([key, val]) => {
                        // skip if blank or default
                        if (
                          val === ''
                        ) {
                          return null;
                        }
                        return (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => setAdvancedFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                    </div>
            </div>

            <PhoneNumberActionConfirmModal
              isOpen={showBuyConfirm}
              onClose={() => setShowBuyConfirm(false)}
              onConfirm={() => {
                setShowBuyConfirm(false);
                handleBuyNewNumber();
              }}
            />

            {
              showEditPhoneNumberModal && (
                <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                        <div style={{position: "fixed", left: "15%", top: "2.5%", width: "70%", height: "95%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px",
                        zIndex: "999999999"}}>
                          <div className="flex items-center justify-between p-3">
                            <h2 className="text-lg font-medium text-gray-900">Configure Phone Number</h2>
                            <div style={{position: "absolute", right: "15px", top: "15px", cursor: "pointer"}} onClick={()=>{setShowEditPhoneNumberModal(false);}}><FaTimes style={{color: "slategray"}}/></div>
                          </div>
                            <div style={{overflowY: "auto", height: "calc(100% - 58px)", borderRadius: "10px"}}>
                              <hr/>
                              <CreatePhoneNumberModal selectedPhoneNumber={selectedPhoneNumber} selectedAliasName={selectedAliasName} onCreatePhoneNumber={onCreatePhoneNumber} clientID={client_id} serviceID={service_id} email={email} role={role} onClose={()=>setShowEditPhoneNumberModal(false)} />
                            </div>
                        </div>
                    </div>
              )
            }

            

            {role === 'superadmin' && manageNumber && <ManagePhoneNumberModal
              number={manageNumber.number} clientID={client_id} serviceID={service_id} email={email}
              onClose={() => setManageNumber(null)}
              onSaved={(message) => {
                setManageNumber(null);
                if (page === 1) {
                  get_phone_numbers_page_details(client_id, service_id, 1);
                } else {
                  setPage(1);
                }
                showToast(message, 'success');
              }}
            />}
            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm" style={{height: "52vh", overflowY: "auto"}}>
                {
                    loading ? (
                        <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                            <div className="loader"></div>
                            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead style={{ position: "sticky", top: "0" }}>
                                <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Phone Number</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Alias Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Agent ID</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Routing</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Provider</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Managed By</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Change Frequency (Days)</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Last Number Changed At</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Updated At</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredPhoneNumbers.map((phoneNumber) => (
                                <tr key={phoneNumber.phone_number} className="hover:bg-gray-50">
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-blue-600" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.phone_number}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.alias_name}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.agent_id}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.routing_mode === 'ivr' ? 'IVR' : 'Direct'}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.provider}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.managed_by}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{phoneNumber.phone_number_change_frequency ?? 'NA'}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatEpochOrNA(phoneNumber.last_number_changed_at)}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatEpochOrNA(phoneNumber.updatedat)}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm">
                                      <div className="flex gap-3">
                                        <button type="button" aria-label={`Edit settings for ${phoneNumber.phone_number}`} className="text-blue-600" onClick={() => handleEditPhoneNumber(phoneNumber)}><Edit size={16} /></button>
                                        {role === 'superadmin' && <button type="button" aria-label={`Delete ${phoneNumber.phone_number}`} className="text-red-700" onClick={() => setManageNumber({ number: phoneNumber })}><Trash2 size={16} /></button>}
                                      </div>
                                    </td>
                                </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
            </div>
            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <div onClick={handlePreviousPage} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Previous
                </div>
                <div onClick={handleNextPage} className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Next
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(50*(page-1))+1}</span> to <span className="font-medium">{totalRecords <= 50 ? totalRecords : 50*page}</span> of <span className="font-medium">{totalRecords}</span> records
                  </p>
                </div>
                <div>
                  <nav className="z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <div onClick={handlePreviousPage} className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                      Previous
                    </div>
                    <div onClick={handleNextPage} className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                      Next
                    </div>
                  </nav>
                </div>
              </div>
            </div>
            </main>}
        <style jsx>
            {`
            .sections-headings {
                            cursor: pointer;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 15px;
                            border-radius: 10px;
                            transition: background 0.3s;
                            width: 100%;
                            }

                            .section-title {
                            font-size: 15px;
                            font-weight: 500;
                            display: flex;
                            align-items: center;
                            }
            `}              
          </style>
        </div>
    )
}

export default PhoneNumberRecords
